/**
 * Portal.io Cache Service
 * Manages caching of Portal.io data to improve performance
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

export class PortalioCacheService {
  private static instance: PortalioCacheService;
  private cache: Map<string, CacheEntry<any>> = new Map();
  
  // Default TTL values (in milliseconds)
  private readonly DEFAULT_PROPOSAL_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly DEFAULT_INSTANCE_TTL = 3 * 60 * 1000; // 3 minutes
  private readonly DEFAULT_SEARCH_TTL = 10 * 60 * 1000; // 10 minutes
  
  private constructor() {
    // Start cleanup interval
    this.startCleanupInterval();
  }

  static getInstance(): PortalioCacheService {
    if (!PortalioCacheService.instance) {
      PortalioCacheService.instance = new PortalioCacheService();
    }
    return PortalioCacheService.instance;
  }

  /**
   * Get cached proposal data
   */
  getProposal(proposalId: number): any | null {
    const key = `proposal_${proposalId}`;
    return this.get(key);
  }

  /**
   * Cache proposal data
   */
  setProposal(proposalId: number, data: any, ttl?: number): void {
    const key = `proposal_${proposalId}`;
    this.set(key, data, ttl || this.DEFAULT_PROPOSAL_TTL);
  }

  /**
   * Get cached instances for a floor plan
   */
  getInstances(floorPlanId: string, optionId?: number): any | null {
    const key = optionId ? `instances_${floorPlanId}_${optionId}` : `instances_${floorPlanId}`;
    return this.get(key);
  }

  /**
   * Cache instances data
   */
  setInstances(floorPlanId: string, data: any, optionId?: number, ttl?: number): void {
    const key = optionId ? `instances_${floorPlanId}_${optionId}` : `instances_${floorPlanId}`;
    this.set(key, data, ttl || this.DEFAULT_INSTANCE_TTL);
  }

  /**
   * Invalidate instances cache when mapping changes
   */
  invalidateInstances(floorPlanId: string): void {
    // Remove all instance caches for this floor plan
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith(`instances_${floorPlanId}`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Invalidate proposal cache
   */
  invalidateProposal(proposalId: number): void {
    const key = `proposal_${proposalId}`;
    this.cache.delete(key);
  }

  /**
   * Invalidate all cache for a floor plan (when proposal changes)
   */
  invalidateFloorPlanData(floorPlanId: string): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      // Remove all instances and related data for this floor plan
      if (key.startsWith(`instances_${floorPlanId}`) || 
          key.includes(floorPlanId)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get cached search results
   */
  getSearchResults(query: string, category?: string): any | null {
    const key = `search_${query}_${category || 'all'}`;
    return this.get(key);
  }

  /**
   * Cache search results
   */
  setSearchResults(query: string, data: any, category?: string, ttl?: number): void {
    const key = `search_${query}_${category || 'all'}`;
    this.set(key, data, ttl || this.DEFAULT_SEARCH_TTL);
  }

  /**
   * Get cached mapping memory
   */
  getMappingMemory(brand: string, model: string): any | null {
    const key = `mapping_${brand}_${model}`;
    return this.get(key);
  }

  /**
   * Cache mapping memory
   */
  setMappingMemory(brand: string, model: string, productId: string): void {
    const key = `mapping_${brand}_${model}`;
    this.set(key, productId, 30 * 60 * 1000); // 30 minutes
  }

  /**
   * Generic get method
   */
  private get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    // Check if cache is expired
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Generic set method
   */
  private set<T>(key: string, data: T, ttl: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  /**
   * Clear all cache
   */
  clearAll(): void {
    this.cache.clear();
  }

  /**
   * Clear expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > entry.ttl) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Start cleanup interval
   */
  private startCleanupInterval(): void {
    // Run cleanup every minute
    setInterval(() => this.cleanup(), 60 * 1000);
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// Export singleton instance
export const portalioCache = PortalioCacheService.getInstance();